/**
 * DataSource: Estrutura (barragens / `dams`) — GUPM-167
 * -----------------------------------------------------
 * Ponto único de leitura da tabela `dams`. Antes (GUPM-134), os hooks
 * `useDams`/`useDamId`/`useAvailableDams` chamavam `supabase.from('dams')`
 * direto, violando a regra inviolável #2 (UI/hook nunca fala com Supabase
 * direto — sempre via adapter). Este datasource concentra esse I/O.
 *
 * Não é POC-manual aguardando integração externa: `dams` é cadastro nativo
 * do app (não há API de terceiro prevista, ao contrário de instrumentação/
 * câmeras/sirenes). Por isso `mode: 'manual'` sem `pendingIntegration`, no
 * mesmo espírito de `auditoria`/`shiftHandover`.
 */

import { supabase } from '@/lib/supabase';
import type { DataSourceMeta } from './types';

/** Barragem exposta às telas (id + código curto A/B/C + nome). */
export interface DamOption {
  id: string;
  code: string;
  name: string;
}

/**
 * Sentinel de UI para "barragem ainda não identificada" no `<select>` de abertura
 * (GUPM-186). Não é um `dams.id` — o boundary do modal o traduz para `dam_id = null`
 * antes de persistir. Distinto de `''` (nada selecionado): "não identificada" é uma
 * escolha válida que habilita a abertura.
 */
export const DAM_UNIDENTIFIED = '__unidentified__';

// ── Contrato ────────────────────────────────────────────────
export interface EstruturaDataSource extends DataSourceMeta {
  /** Barragens `is_active=true`, ordenadas por `code`. Alimenta o filtro de estrutura. */
  listActiveDams(): Promise<DamOption[]>;
  /** Resolve o uuid de `dams.id` a partir do `code` (A/B/C). `null` se não existir. */
  getDamIdByCode(code: string): Promise<string | null>;
  /** Barragens cujos `code` estão em `codes`, ordenadas por `code`. */
  listDamsByCodes(codes: string[]): Promise<DamOption[]>;
}

// ── Implementação (leitura direta do Supabase) ──────────────
export const supabaseEstrutura: EstruturaDataSource = {
  mode: 'manual',

  async listActiveDams() {
    const { data, error } = await supabase
      .from('dams')
      .select('id, code, name')
      .eq('is_active', true)
      .order('code')
      .returns<DamOption[]>();
    if (error) throw error;
    return data ?? [];
  },

  async getDamIdByCode(code) {
    const { data, error } = await supabase
      .from('dams')
      .select('id')
      .eq('code', code)
      .limit(1)
      .maybeSingle<{ id: string }>();
    if (error) throw error;
    return data?.id ?? null;
  },

  async listDamsByCodes(codes) {
    const { data, error } = await supabase
      .from('dams')
      .select('id, code, name')
      .in('code', codes)
      .order('code')
      .returns<DamOption[]>();
    if (error) throw error;
    return data ?? [];
  },
};
